import type { JSMessage } from '@/shared/utils/jsMessageHandler'

/**
 * ET-1.5: нормализованный результат wallet-эндпоинтов.
 * Единственное место, где живут все три «правописания» успеха легаси-бэкенда:
 *   1. `result === 'success'`        (легаси-конверт /wallet/withdraw, /wallet/send)
 *   2. `message.type === 'success'`  (JSMessage-конверт)
 *   3. `success === true`            (новый конверт /wallet/exchange)
 */
export type WalletResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; error: string; message: string; data?: unknown }

/** Сырая форма ответа wallet-эндпоинтов (все поля опциональны и не доверяются). */
interface RawWalletResponse {
  success?: unknown
  result?: unknown
  /** Строка или JSMessage-объект с `.text` */
  message?: unknown
  error?: unknown
  data?: unknown
}

const extractMessage = (message: unknown): string => {
  if (typeof message === 'string') {
    return message
  }

  if (message && typeof message === 'object' && typeof (message as JSMessage).text === 'string') {
    return (message as JSMessage).text
  }

  return ''
}

const isSuccessMessage = (message: unknown): boolean =>
  Boolean(message) && typeof message === 'object' && (message as JSMessage).type === 'success'

export function parseWalletResponse<T = unknown>(raw: unknown): WalletResult<T> {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, error: 'unknown_error', message: '' }
  }

  const response = raw as RawWalletResponse

  const isSuccess =
    response.success === true ||
    response.result === 'success' ||
    isSuccessMessage(response.message)

  if (isSuccess) {
    return { ok: true, data: response.data as T }
  }

  return {
    ok: false,
    error: typeof response.error === 'string' && response.error !== '' ? response.error : 'unknown_error',
    message: extractMessage(response.message),
    // Проносим как есть: для exchange_rate_changed здесь свежий ExchangeRateInfo
    data: response.data,
  }
}
